<template>
  <PageHeader data-test="hire-invitation-page">
    <template #header-breadcrumbs>
      <HireHeaderBreadcrumbs
        class="tw-mb-4"
        :back-url="laravelNamedRoute('frontend.dashboard.dashboard')"
        :back-label="$t('Home')"
        :number="invitation?.number"
      />
    </template>
    <template #header-title>
      <div v-if="!loading" class="tw-line-clamp-2">
        {{ invitation?.jobTitle ?? $t('Hire overview') }}
      </div>
    </template>
    <template #header-description>
      <div v-if="loading" class="tw-w-1/2">
        <Loading title />
      </div>
      <HireHeaderSummary
        v-else-if="invitation"
        :status="HireActiveStatus.Draft"
        :start="startDate.date.value"
        :end="endDateLabel"
        :company="invitation.company"
      />
    </template>
  </PageHeader>

  <WdsStack v-if="!loading && !invitation" class="tw-w-full">
    <ContentLoadingErrorNotice
      :title="$t('This hire is not available')"
      :description="$t('There is nothing for you to add on this hire right now.')"
      hide-status-button
    />
  </WdsStack>

  <WdsAlert
    v-if="invitation && !invitation.requiredCustomFieldsComplete"
    info
    icon
    bordered
    class="tw-mb-3"
    data-test="hire-invitation-draft-notice"
  >
    {{ draftMessage }}
  </WdsAlert>

  <WdsStack v-if="invitation" gap-custom="40px" class="tw-w-full">
    <WdsStack class="tw-w-3/5" direction-column gap-lg>
      <WdsStack direction-column gap-sm class="tw-rounded">
        <HirePageSectionHeader>
          <template #title>
            <WdsHeading class="tw-pl-1 tw-text-lg" type="title3">
              {{ $t('Contract summary') }}
            </WdsHeading>
          </template>
        </HirePageSectionHeader>
        <div class="tw-grid tw-grid-cols-3 tw-gap-4 tw-px-2 tw-pt-2">
          <template v-for="row in detailRows" :key="row.label">
            <WdsText class="tw-font-medium">
              <span class="tw-text-gray-900">{{ row.label }}</span>
            </WdsText>
            <WdsText :secondary="!row.value" class="tw-text-gray-900 tw-col-span-2">
              <span class="tw-font-normal tw-whitespace-pre-line">{{ row.value || $t('Not specified') }}</span>
            </WdsText>
          </template>
        </div>
      </WdsStack>

      <WdsStack direction-column class="tw-rounded">
        <HirePageSectionHeader>
          <template #title>
            <WdsHeading type="title3" class="tw-pl-2">
              {{ $t('Additional information') }}
            </WdsHeading>
          </template>
        </HirePageSectionHeader>
        <HireCustomFieldsGroup
          class="tw-px-2"
          data-test="hire-custom-fields"
          :title="$t('Details requested by :company', { company: invitation.companyName ?? '' })"
          :sections="[
            { key: 'questions', fields: invitation.customFields, values: invitation.customFieldValues },
          ]"
          :editable="invitation.answerableCustomFields.length > 0"
          edit-data-test="hire-invitation-answer"
          @edit="modalOpen = true"
        />
      </WdsStack>
    </WdsStack>

    <WdsStack class="tw-w-2/5 tw-h-max tw-rounded" direction-column gap-md>
      <HireStatusBox :title="$t('Overview')">
        <div class="tw-mb-4 tw-px-2">
          <div
            class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-rounded-md tw-w-full tw-bg-gray-600"
          >
            <WdsText>
              <span class="tw-text-white">{{ $t('Draft') }}</span>
            </WdsText>
          </div>
        </div>
        <HireComplianceGateStatus :compliances="active" :loading="gateLoading" :hire-id="hireId" />
      </HireStatusBox>
    </WdsStack>
  </WdsStack>

  <HireWorkerCustomFieldsModal v-model:open="modalOpen" :hire-id="hireId" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsAlert, WdsHeading, WdsStack, WdsText } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { ComplianceName, HireActiveStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import PageHeader from '@/components/PageHeader.vue'
import Loading from '@/components/Dashboard/Widgets/Utils/Loading.vue'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import HireComplianceGateStatus from '@/components/Hire/HirePageCompliance/HireComplianceGateStatus.vue'
import HireWorkerCustomFieldsModal from '@/components/Hire/HirePageCompliance/modals/HireWorkerCustomFieldsModal.vue'
import { useComplianceGate } from '@/components/Hire/HirePageCompliance/useComplianceGates'
import HireCustomFieldsGroup from '@/components/Hire/HirePageParts/HireCustomFieldsGroup.vue'
import HireHeaderBreadcrumbs from '@/components/Hire/HirePageParts/HireHeaderBreadcrumbs.vue'
import HireHeaderSummary from '@/components/Hire/HirePageParts/HireHeaderSummary.vue'
import HirePageSectionHeader from '@/components/Hire/HirePageParts/HirePageSectionHeader.vue'
import HireStatusBox from '@/components/Hire/HirePageParts/HireStatusBox.vue'

// A draft hire as its worker sees it: the hire page's own building blocks, minus anything commercial.
const props = defineProps<{ hireId: string }>()

const { result, loading } = useQuery(
  graphql(`
    query HireInvitationPage($id: ID!, $viewerAccount: ID!) {
      hireInvitation(id: $id) {
        hireId
        number
        company {
          id
          name
          avatar
        }
        companyName
        jobTitle
        jobDescription
        startDate
        endDate
        workLocation
        requiredCustomFieldsComplete
        customFields {
          ...FieldableFormCustomField
        }
        answerableCustomFields {
          id
        }
        customFieldValues {
          ...CustomFieldValuesView
        }
      }
    }
  `),
  () => ({ id: props.hireId, viewerAccount: currentAccount.globalId }),
  { clientId: 'publicApi' },
)

const invitation = computed(() => result.value?.hireInvitation ?? null)
const modalOpen = ref(false)

const { active, loading: gateLoading } = useComplianceGate(
  props.hireId,
  ComplianceName.GateHireOffer,
)

const startDate = useDate(computed(() => invitation.value?.startDate ?? null))
const endDate = useDate(computed(() => invitation.value?.endDate ?? null))
const endDateLabel = computed(() => (invitation.value?.endDate ? endDate.date.value : $t('No end date')))

const draftMessage = computed(() =>
  $t(':company needs some details from you before they can send an offer.', {
    company: invitation.value?.companyName ?? '',
  }),
)

const detailRows = computed(() => [
  { label: $t('Client'), value: invitation.value?.companyName },
  { label: $t('Title'), value: invitation.value?.jobTitle },
  { label: $t('Job description'), value: invitation.value?.jobDescription },
  {
    label: $t('Dates'),
    value: startDate.isValid.value ? `${startDate.date.value} - ${endDateLabel.value}` : null,
  },
  { label: $t('Work address'), value: invitation.value?.workLocation },
])
</script>
